import { useCallback, useEffect, useState } from "react";
import { Image, StyleSheet, Text, useWindowDimensions, View } from "react-native";
import { getPublishedCentralMemorial, type PublicCentralMemorial } from "../lib/public-central-memorial";
import { central, spacing } from "../theme/tokens";
import { PageShell } from "./layout";
import { WeHonorLogomark } from "./logomark";
import { PublicAttendanceCheckIn } from "./PublicAttendanceCheckIn";
import { Button, CentralSurface, LoadingState } from "./ui";

/** Public, event-scoped tablet surface. It never reads private guest lists. */
export function PublicRegisterScreen({ funeralHomeId, slug, eventId }: { funeralHomeId: string; slug: string; eventId: string }) {
  const { width, height } = useWindowDimensions();
  const landscape = width >= 900;
  const compact = height < 740;
  const compactPhone = compact && !landscape;
  const funeralHomeName = ["fh-monarch", "fh-preview-3fea5d3393b2"].includes(funeralHomeId) ? "Monarch Funeral Home" : "the funeral home";
  const [photoFailed, setPhotoFailed] = useState(false);
  const [photoRatio, setPhotoRatio] = useState(0.75);
  const [signingHeight, setSigningHeight] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [addressEditing, setAddressEditing] = useState(false);
  const complete = useCallback(() => setCompleted(true), []);
  const reset = useCallback(() => { setCompleted(false); setAddressEditing(false); }, []);
  useEffect(() => setSigningHeight(0), [width, height, funeralHomeId, slug, eventId]);
  const [memorial, setMemorial] = useState<PublicCentralMemorial | null>(null);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setMemorial(null);
    setPhotoFailed(false);
    setError("");
    if (!funeralHomeId || !slug || !eventId) { setError("Open the tablet link for a specific service from WeHonor Central."); return; }
    void getPublishedCentralMemorial(funeralHomeId, slug).then((value) => {
      if (active) setMemorial(value);
    }).catch(() => { if (active) setError("This register could not be opened. Retry, or ask the funeral home to check that the service is published."); });
    return () => { active = false; };
  }, [funeralHomeId, slug, eventId, attempt]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const previousTitle = document.title;
    document.title = "WeHonor Guest Register";
    const metas = ([["robots", "noindex,nofollow,noarchive"], ["referrer", "no-referrer"]] as const).map(([name, content]) => {
      const existing = document.querySelector(`meta[name="${name}"]`);
      const element = existing ?? document.createElement("meta");
      const previous = element.getAttribute("content");
      element.setAttribute("name", name);
      element.setAttribute("content", content);
      if (!existing) document.head.appendChild(element);
      return () => { if (!existing) element.remove(); else if (previous === null) element.removeAttribute("content"); else element.setAttribute("content", previous); };
    });
    return () => { document.title = previousTitle; metas.forEach((restore) => restore()); };
  }, []);

  const event = memorial?.serviceEvents.find((candidate) => candidate.id === eventId);
  const available = Boolean(memorial?.checkInEnabled && event && event.registerStatus !== "closed" && event.registerStatus !== "draft" && (!event.checkInClosesAt || Date.parse(event.checkInClosesAt) > Date.now()));
  const portrait = memorial?.photoUrls[0];
  useEffect(() => {
    let active = true;
    setPhotoRatio(0.75);
    if (portrait) Image.getSize(portrait, (imageWidth, imageHeight) => {
      if (active && imageWidth > 0 && imageHeight > 0) setPhotoRatio(imageWidth / imageHeight);
    }, () => {});
    return () => { active = false; };
  }, [portrait]);
  const portraitHeight = landscape ? Math.min(360, height * 0.48) : compact ? !landscape ? width < 360 ? 80 : 96 : 164 : width >= 600 ? 300 : 220;
  const portraitWidth = Math.min(portraitHeight * photoRatio, landscape ? 320 : width - 48);
  const portraitStyle = { width: portraitWidth, height: portraitWidth / photoRatio };
  return <CentralSurface><PageShell contentStyle={{ paddingBottom: 0 }} edgeToEdge showBrandBar={false} showDecorativeBackdrop={false} backgroundColor={central.surface.canvas}>
    <View style={[styles.page, { minHeight: height }, compact && styles.pageCompact, landscape && styles.pageWide]}>
      {error ? <View style={styles.message}><Text accessibilityLiveRegion="assertive" style={styles.body}>{error}</Text><Button label="Retry" onPress={() => setAttempt(value => value + 1)} shape="rounded" /></View> : !memorial ? <LoadingState label="Opening the Register…" /> : !available ? <View style={styles.message}><Text accessibilityRole="header" style={[styles.name, { color: central.text.primary }]}>Register Unavailable</Text><Text style={styles.body}>This service is not accepting check-ins. Please ask a member of staff for help.</Text></View> : <View style={[styles.register, compact && styles.registerCompact, landscape && styles.registerWide]}>
        <View style={[styles.remembrance, compactPhone && styles.remembrancePhone, landscape && styles.remembranceWide]}>
          {portrait && !photoFailed ? <View style={[styles.portraitFrame, portraitStyle]}>
            <Image source={{ uri: portrait }} accessibilityLabel={`Portrait of ${memorial.lovedOneName}`} onError={() => setPhotoFailed(true)} resizeMode="contain" style={styles.portrait} />
          </View> : null}
          <View style={[styles.identity, compact && styles.identityCompact, compactPhone && styles.identityPhone]}>
            <Text accessibilityRole="header" style={[styles.name, !landscape && styles.nameNarrow, compact && styles.nameCompact, compactPhone && styles.namePhone]}>{memorial.lovedOneName}</Text>
            {event?.birthDate || event?.deathDate ? <Text style={styles.detail}>{[event.birthDate?.slice(0, 4), event.deathDate?.slice(0, 4)].filter(Boolean).join(" — ")}</Text> : null}
          </View>
        </View>
        <View style={[styles.signing, { minHeight: completed || addressEditing ? signingHeight : 0 }]} onLayout={({ nativeEvent }) => { if (!completed && !addressEditing) setSigningHeight(Math.ceil(nativeEvent.layout.height)); }}>
          <PublicAttendanceCheckIn key={`${funeralHomeId}/${slug}/${eventId}`} kiosk inlineRegister funeralHomeName={funeralHomeName} lovedOneName={memorial.lovedOneName} onComplete={complete} onReset={reset} onAddressEditingChange={setAddressEditing} funeralHomeId={funeralHomeId} slug={memorial.slug} eventId={eventId} eventLabel={event?.title || event?.name || "Memorial Service"} source="manual" />
        </View>
      </View>}
      <View accessible accessibilityLabel="Guest register by WeHonor" style={styles.brand}><WeHonorLogomark size={18} color={central.text.muted} accentColor={central.text.muted} /><Text style={styles.wordmark}>wehonor</Text></View>
    </View>
  </PageShell></CentralSurface>;
}

const styles = StyleSheet.create({
  page: { alignSelf: "center", justifyContent: "flex-start", width: "100%", maxWidth: 1060, paddingHorizontal: 20, paddingVertical: 14, gap: 20 },
  pageCompact: { paddingHorizontal: 16, paddingVertical: 10, gap: 10 },
  pageWide: { paddingHorizontal: 40, paddingVertical: 32 },
  register: { width: "100%", alignItems: "center", gap: 24 },
  registerCompact: { gap: 10 },
  registerWide: { flexDirection: "row", alignItems: "center", gap: 48 },
  remembrance: { width: "100%", alignItems: "center", gap: 14 },
  remembrancePhone: { flexDirection: "row", gap: 14 },
  identityPhone: { flex: 1, minWidth: 0, alignItems: "flex-start" },
  namePhone: { fontSize: 26, lineHeight: 28, textAlign: "left" },
  remembranceWide: { flex: 1, minWidth: 0, gap: 20 },
  portraitFrame: { flexShrink: 0, alignSelf: "center", overflow: "hidden", borderWidth: 1, borderColor: central.border.subtle, borderRadius: 4 },
  portrait: { width: "100%", height: "100%" },
  identity: { width: "100%", alignItems: "center", gap: 6 },
  identityCompact: { gap: 4 },
  name: { color: central.text.primary, fontFamily: "Newsreader_600SemiBold", fontSize: 42, lineHeight: 46, textAlign: "center" },
  nameNarrow: { fontSize: 32, lineHeight: 36 },
  nameCompact: { fontSize: 28, lineHeight: 32 },
  detail: { color: central.text.muted, fontFamily: central.typography.body, fontSize: 13, lineHeight: 19, textAlign: "center" },
  service: { color: central.text.muted, fontFamily: central.typography.bodyMedium, fontSize: 12, lineHeight: 18, textAlign: "center" },
  signing: { flex: 1, minWidth: 0, width: "100%", maxWidth: 520, justifyContent: "center" },
  brand: { flexGrow: 1, minHeight: 18, flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 7 },
  wordmark: { color: central.text.muted, fontFamily: central.typography.label, fontSize: 14 },
  message: { gap: spacing.md, paddingVertical: spacing.xl },
  body: { color: central.text.secondary, fontFamily: central.typography.body, fontSize: 16, lineHeight: 25 },
});
